/**
 * IntegrationsConnectList — connect/connected grid for every integration a
 * deployed pipeline uses (required AND optional).
 *
 * Renders the EXACT same surface as the marketplace detail modal's
 * "Integrations" section — same markup + same `mkt-modal-integration*` CSS
 * classes (a 2-column grid of brand rows) — so the two look identical. We import
 * the modal's stylesheet here so the shared classes are present on the pipeline
 * page too, and reuse the same atoms/engine:
 *   - <IntegrationLogo>        — per-provider brand mark atom.
 *   - useIntegrationConnect()  — the shared Connect engine (OAuth popups +
 *                                Gitlab/Lark form modals + refetch-on-complete).
 *   - GET /workflows/{uuid}/integrations/status — per-workflow status (same
 *                                shape as the marketplace integration-status),
 *                                so the list is fully dynamic for whatever a
 *                                template declares (slack/lark/notion/jira…).
 *
 * Status shape: { required, connected, missing, optional, optionalConnected,
 * optionalMissing, integrations }. Entries are a provider-id string or an
 * `{ any: [...] }` OR-group; `integrations[id]` = { id, name, connectPath }.
 *
 * SELECTABLE mode (per-agent integration allowlist / least-privilege):
 *   When `selectable` is set, each CONNECTED row doubles as a toggle for "use
 *   this integration in THIS agent". There is NO separate section — the green
 *   connected-tick means "connected", and HIGHLIGHTING THE WHOLE ROW means
 *   "selected to be used by this agent". Required-connected rows are locked ON.
 *   A single "Save access" button (shown only when the selection changed) PATCHes
 *   /workflows/{uuid} with `enabledIntegrations` (null when everything is on, so
 *   the row reverts to the all-connected default). Absent `selectable` → the list
 *   is read-only (e.g. the trigger modal).
 */

import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { IonSpinner } from '@ionic/react';
import api from '../../../services/api';
import useIntegrationConnect from '../../../hooks/useIntegrationConnect';
import IntegrationRow from './IntegrationRow';
import {
  connectedProviders as computeConnected,
  initialSelection as computeInitialSelection,
  toggleableSkills as computeToggleableSkills,
  isLocked as computeIsLocked,
} from '../../../utils/enabledIntegrations';
import { multiRoleProviders } from '../../../utils/integrationRoles';
import { buildIntegrationRows, mergedRowLabel } from '../../../utils/integrationRows';
// Reuse the marketplace modal's integration styles verbatim (2-col grid + row
// cards). CRA bundles CSS globally + dedupes, so this just guarantees the
// shared classes are loaded on the pipeline detail page.
import '../../../pages/MarketplacePage/MarketplaceDetailModal.css';
import './IntegrationsConnectList.css';

const IntegrationsConnectList = ({
  workflowUuid,
  // Selection (allowlist) mode — opt-in. When false the list is read-only.
  selectable = false,
  stored = null,        // the workflow row's current enabledIntegrations (or null)
  setToast,
  onSaved,
  // Surface every status (re)fetch to the parent — fires on the initial load
  // AND after each in-place Connect (refreshKey bump), so a host that gates an
  // action on required-connected state (e.g. the trigger modal's Trigger
  // button) updates live the moment the user connects. Receives the raw
  // status payload (or null on reset/failure).
  onStatusChange,
}) => {
  const { refreshKey, handleConnectClick, connectModals } = useIntegrationConnect();

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Selection state (only used when `selectable`). Seeded from the workflow's
  // stored allowlist once status loads; required-connected are always in.
  // Toggling auto-saves (no Save button): optimistic UI + a single in-flight
  // PATCH. While a save is in flight EVERY toggle is blocked and the saving row
  // shows a spinner (`savingId`) — so a rapid clicker can't fire ten toggles a
  // second; each change commits before the next is allowed. `savedRef` holds the
  // last persisted selection (for revert on failure).
  const [selected, setSelected] = useState(() => new Set());
  const [savingId, setSavingId] = useState(null); // id mid-save (null = idle)
  const savedRef = useRef(new Set());

  // Latest onStatusChange in a ref so the fetch effect doesn't re-run (and
  // refetch) just because the parent recreated the callback on a render.
  const onStatusChangeRef = useRef(onStatusChange);
  useEffect(() => { onStatusChangeRef.current = onStatusChange; }, [onStatusChange]);

  useEffect(() => {
    if (!workflowUuid) {
      setStatus(null);
      onStatusChangeRef.current?.(null);
      return undefined;
    }
    let cancelled = false;
    if (refreshKey === 0) setLoading(true);
    setError(null);
    api
      .get(`/workflows/${encodeURIComponent(workflowUuid)}/integrations/status`)
      .then((res) => {
        if (cancelled) return;
        setStatus(res || null);
        onStatusChangeRef.current?.(res || null);
      })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load integration status'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [workflowUuid, refreshKey]);

  // Derived selection context (stable per status/stored).
  const connectedSet = useMemo(() => computeConnected(status), [status]);
  const baseline = useMemo(
    () => computeInitialSelection(status, Array.isArray(stored) ? stored : null),
    [status, stored],
  );

  // (Re)seed the working selection whenever the baseline changes (status load /
  // a successful save bumping `stored`). Only in selectable mode.
  useEffect(() => {
    if (selectable && status) {
      setSelected(new Set(baseline));
      savedRef.current = new Set(baseline);
    }
  }, [selectable, status, baseline]);

  // Persist a selection (auto-save — no Save button). ONE save at a time: the
  // toggle guard blocks further clicks while `savingId` is set, so we never race
  // two PATCHes. Everything connected selected → send null to CLEAR the allowlist
  // (revert to the all-connected default); a strict subset → send the explicit
  // list. Required is force-included server-side regardless. On failure: revert
  // the optimistic change to the last persisted selection + toast.
  const persist = useCallback(async (sel, id) => {
    if (!workflowUuid) return;
    setSavingId(id);
    try {
      const isStrictSubset = sel.size < connectedSet.size;
      const payload = { enabledIntegrations: isStrictSubset ? [...sel] : null };
      const res = await api.patch(`/workflows/${encodeURIComponent(workflowUuid)}`, payload);
      savedRef.current = new Set(sel);
      onSaved?.(res?.enabledIntegrations ?? null);
    } catch (err) {
      setSelected(new Set(savedRef.current));
      setToast?.({ show: true, message: err?.message || 'Failed to update integration access', color: 'danger' });
    } finally {
      setSavingId(null);
    }
  }, [workflowUuid, connectedSet, setToast, onSaved]);

  // A click TOGGLES and SAVES in one step — optimistic UI, single background
  // PATCH. BLOCKED while another save is in flight (savingId) — that's the
  // anti-spam guard: you can't fire ten toggles a second. Selecting is otherwise
  // always allowed; DEselecting is blocked when the row is locked — a hard-
  // required provider, or the LAST still-selected member of a required OR-group
  // (the row shows a lock there, so this no-op matches what the user sees).
  const toggle = useCallback((id) => {
    if (savingId !== null) return; // a save is in flight — ignore until it lands
    const deselecting = selected.has(id);
    if (deselecting && computeIsLocked(status, selected, id)) return;
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
    persist(next, id);
  }, [savingId, status, selected, persist]);

  // One row via the SHARED <IntegrationRow> (same component the deploy modal
  // uses, so the two behave identically). No deploy gate here
  // (highlightMissing=false).
  // Providers a workflow needs for MORE THAN ONE purpose (e.g. Lark as both a
  // doc source AND a chat target) get role-qualified card labels ("Lark Docs" vs
  // "Lark Chat") so the otherwise-identical cards read distinctly. Derived from
  // the GROUP purposes across required+optional; single-role providers stay plain.
  const multiRoleSet = useMemo(
    () => multiRoleProviders(status?.required, status?.optional),
    [status],
  );

  // MERGED flat row model — required + optional entries expanded (one row per
  // OR-group member) then DEDUPED by provider id, so a provider appearing in
  // several role-groups (Lark: required chat + optional docs) draws exactly ONE
  // switch — one id, one connection, one allowlist entry ("Lark · Chat + Docs").
  // Port of the self-host dashboard's mergeDuplicateIds; see integrationRows.js.
  const mergedRows = useMemo(
    () => buildIntegrationRows(
      status?.required,
      status?.optional,
      status?.connected,
      status?.optionalConnected,
    ),
    [status],
  );

  const renderMergedRow = useCallback((row) => {
    if (!status) return null;
    const meta = status.integrations?.[row.id] || { id: row.id, name: row.id, connectPath: '/integrations' };
    return (
      <IntegrationRow
        key={`int:${row.id}`}
        id={row.id}
        name={mergedRowLabel(meta.name, row, multiRoleSet)}
        connectPath={meta.connectPath}
        connected={row.connected}
        selectable={selectable}
        selected={selectable && selected.has(row.id)}
        // Only a row with a REQUIRED-section occurrence can lock — a purely
        // optional row must never inherit the required lock (the merged Lark
        // row IS a required member, so it locks correctly when it's the one
        // holding its OR-group).
        locked={selectable && row.requiredMember && computeIsLocked(status, selected, row.id)}
        busy={selectable && savingId === row.id}
        optional={row.optional}
        onToggle={toggle}
        onConnect={handleConnectClick}
      />
    );
  }, [status, handleConnectClick, selectable, selected, savingId, toggle, multiRoleSet]);

  // Dynamic "what this agent can use" list — the selected/enabled connected
  // integrations, by display name. Updates live as the user toggles rows.
  const usingNames = useMemo(() => {
    if (!status) return [];
    const skillNames = new Map(computeToggleableSkills(status).map((s) => [s.id, s.name || s.id]));
    return [...selected]
      .map((id) => status.integrations?.[id]?.name || skillNames.get(id) || id)
      .sort((a, b) => a.localeCompare(b));
  }, [status, selected]);

  // No-connection toggleable skills (e.g. codebase-memory) the workflow declares.
  // Rendered as toggle-only rows (no Connect). They reuse the SAME selection
  // state + allowlist save path as integration rows.
  const toggleSkills = useMemo(() => computeToggleableSkills(status), [status]);

  const renderToggleableRow = useCallback((skill) => {
    // REQUIRED store skill (required:true — dataset-store / gbrain) → locked ON,
    // reuse the SAME locked row state; never deselectable.
    const skillLocked = selectable && skill.required === true;
    return (
      <IntegrationRow
        key={`skill:${skill.id}`}
        id={skill.id}
        name={skill.name || skill.id}
        description={skill.description || ''}
        noIntegration
        selectable={selectable}
        selected={selectable && (skillLocked || selected.has(skill.id))}
        locked={skillLocked}
        busy={selectable && savingId === skill.id}
        onToggle={toggle}
      />
    );
  }, [selectable, selected, savingId, toggle]);

  const hasRequired = status && Array.isArray(status.required) && status.required.length > 0;
  const hasOptional = status && Array.isArray(status.optional) && status.optional.length > 0;
  const hasToggleable = toggleSkills.length > 0;
  const hasAny = hasRequired || hasOptional || hasToggleable;

  return (
    <>
      {loading ? (
        <div className="int-connect-loading">
          <IonSpinner name="dots" />
          <span>Loading integrations…</span>
        </div>
      ) : error ? (
        <p className="int-connect-error">
          Couldn’t load integration status. You can manage integrations from{' '}
          <a href="/integrations">Integrations</a>.
        </p>
      ) : hasAny ? (
        <>
          {selectable && (
            <p className="int-connect-summary">
              {usingNames.length ? (
                <>This agent can use: <strong>{usingNames.join(', ')}</strong></>
              ) : (
                'This agent uses no integrations yet.'
              )}
            </p>
          )}
          <ul className="mkt-modal-integrations">
            {mergedRows.map(renderMergedRow)}
            {hasToggleable && toggleSkills.map(renderToggleableRow)}
          </ul>
        </>
      ) : (
        <p className="int-connect-none">No skills to configure for this agent.</p>
      )}
      {connectModals}
    </>
  );
};

export default IntegrationsConnectList;
